import { action, observable } from "mobx";
import moment from "moment";
import EventSourcePolyfill from "eventsource";
import HttpUtils from "src/utils/HttpUtils"
import toastService from "../services/ToastService";

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

const eventNames = [
  "TechnicalNotificationEventOnChangedTables",
  "EntityCreated",
  "NewEntityVersionCreated",
  "EntityMarkedForDeletion",
  "EntityDeleted",
  "EntityInactivated",
  "EntityUpdated",
  "EntityApproved",
  "EntityRejected",
  "CoreEntityProposalApproved",
  "CoreEntityProposalRejected",
  "MaintenanceAgencyCreated",
  "FrameworkCreated",
  "FrameworkPublished",
  "ProjectCreated",
  "ProjectReopened",
  "ProjectMadeReadyForPromotion",
  "ProjectPromoted",
  "ProjectClosed",
  "BranchCreated",
  "BranchMerged",
  "ReleaseBuilt",
  "ReleaseDeleted",
  "ReleaseUpdated",
  "BranchInactivated",
  "MaintenanceAgencyOwnershipAssigned",
  "MaintenanceAgencyOwnershipUnassigned",
  "ProjectOwnershipAssigned",
  "ProjectOwnershipUnassigned",
  "BranchOwnershipAssigned",
  "BranchOwnershipUnassigned",
  "DictionaryAdministratorAdded",
  "DictionaryAdministratorRemoved",
  "OrchestrationUpdated",
  "ExcelFileImported",
  "CsvFileImported",
  "FrameworkComparisonGenerated",
  "HeartBeat",
  "ValidationCompleted"
];
export default class EventSourceStore {
  @observable lastEventId;
  @observable lastEventName = "";
  @observable lastEventData = {};

  @observable sseId;
  @observable eventSource;

  constructor(
    userStore,
    contentManagementStore,
    searchStore,
    dictionaryAdministratorStore,
    projectOwnerStore,
    maintenanceAgencyStore,
    systemAdministratorStore,
    createMappingProjectStore,
    createFrameworkProjectStore,
    exportImportStore,
    validationStore
  ) {
    this.userStore = userStore;
    this.contentManagementStore = contentManagementStore;
    this.searchStore = searchStore;
    this.dictionaryAdministratorStore = dictionaryAdministratorStore;
    this.projectOwnerStore = projectOwnerStore;
    this.maintenanceAgencyStore = maintenanceAgencyStore;
    this.systemAdministratorStore = systemAdministratorStore;
    this.createFrameworkProjectStore = createFrameworkProjectStore;
    this.createMappingProjectStore = createMappingProjectStore;
    this.exportImportStore = exportImportStore;
    this.validationStore = validationStore;
    this.loadTimestampInMillis = moment().valueOf();

    if (IS_BIRD_APP) return;

    // subscribe to events
    // this.sseId;
    this.reconnectInterval = 1000;
    // this.lastEventId; // will be updated on receiving events, used to replay events in case of temporal outage
    this.logSseTracing = false;
    if (document.visibilityState === "visible") {
      if (this.logSseTracing) {
        console.log("visible => initializing event source");
      }
      // if visible (focused tab in the browser) subscribe to events
      this.func_initializeEventSource();
    } else {
      // otherwise do not subscribe, we will subscribe on focusing the tab
      if (this.logSseTracing) {
        console.log("hidden => not initializing event source");
      }
    }
  }

  // @action
  func_unsubscribeEventSource = () => {
    if (HttpUtils.getReloadingBeingHandled) {
      return;
    }

    if (this.logSseTracing) {
      console.log("unsubscribing " + this.sseId);
    }

    const url = HttpUtils.getBaseUrl() + "sse/unsubscribe?sse-id=" + this.sseId;

    try {
      const headers = new Headers({
        "Content-Type": "application/json",
        "x-sdd-app": "true",
      });
      window
        .fetch(url, {
          method: "GET",
          credentials: "same-origin",
          headers: HttpUtils.amendHeadersIfNeeded(headers),
          redirect: "manual",
        })
        .then((resp) => {
          if (this.logSseTracing) {
            console.log("response from unsubscribe", resp);
          }
        });
    } catch (error) {
      console.error("error when unsubscribing", error);
    }

    if (this.eventSource) {
      if (this.logSseTracing) {
        console.log("closing");
      }
      try {
        this.eventSource.close();
      } catch (error) {
        console.log("closing event source failed", error);
      }
    }
  };

  // TODO: IE does not support EventSource (tested and works in Chrome and FF),
  // one of the eventsource polyfill libraries can be used to make it work under IE if it's needed
  @action
  func_initializeEventSource = () => {
    if (HttpUtils.getReloadingBeingHandled) {
      return;
    }

    this.sseId = moment().valueOf();

    if (this.logSseTracing) {
      console.log("subscribing: " + this.sseId);
    }

    const url =
      HttpUtils.getBaseUrl() +
      "sse/subscribe?sse-id=" +
      this.sseId +
      "&last-event-id=" +
      (this.lastEventId ? this.lastEventId : "");

    let headers;
    if (process.env.NODE_ENV.includes("development")) {
      headers = {
        "Authenticated-actor-id": this.userStore.getDevUser,
        "x-sdd-app": "true",
      };
    } else {
      headers = {
        "x-sdd-app": "true",
      };
    }

    this.eventSource = new EventSourcePolyfill(url, {
      headers: headers,
    });

    eventNames.forEach((eventName) => {
      this.eventSource.addEventListener(
        eventName,
        function (e) {
          if (this.logSseTracing) console.log("handling event " + eventName);
          this.fetchEventRelatedData(this.parseEvent(e));
        }.bind(this),
        false
      );
    });
    // this.eventSource.addEventListener(
    //   'HeartBeat',
    //   function (e) {
    //     alert('ok')
    //     if(this.logSseTracing) { console.log('heart beat', e);}
    //   }.bind(this),
    //   false
    // );

    // this.eventSource.addEventListener('open', function(e) {
    //     console.log("open...", e);
    // }, false);

    // this.eventSource.addEventListener('error', function(e) {
    //   console.log("error...", e);
    //   console.log(e, EventSource.CLOSED)
    //   if (e.readyState == EventSource.CLOSED) {
    //     // Connection was closed.
    //     if(this.logSseTracing) { console.log("connection closed");}
    //   } else {
    //     console.log("error", e);
    //   }
    // }, false);

    this.eventSource.onerror = (e) => {
      if (e.status === 333 || e.status === 401) {
        console.log("unauthenticated, reloading page", e);
        this.subscribingEnabled = false;
        HttpUtils.reloadPage();
      }

      if (e.status === 501) {
        console.log("SSE disabled", e);
        this.eventSource = null;
        return;
      }

      console.log("error in event source (maybe only timeout)", e);

      if (!this.eventSource) return;

      const reconnectingThreshold = 900000; // cca 30 mins in total (2^n)
      if (this.eventSource.reconnectInterval > reconnectingThreshold) {
        if (this.logSseTracing) {
          console.log("closing after limiting retries");
        }
        try {
          this.eventSource.close();
        } catch (error) {
          console.log("closing event source failed", error);
        }
        return;
      }

      this.eventSource.reconnectInterval =
        this.eventSource.reconnectInterval * 2;
      if (this.logSseTracing) {
        console.log("reconnectInterval", this.eventSource.reconnectInterval);
      }
    };

    // this.eventSource.onmessage = (m) => {
    //   if(this.logSseTracing) { console.log("message", m);}
    // }

    this.eventSource.onopen = (m) => {
      if (this.logSseTracing) {
        console.log("opened", m);
      }
      this.reconnectInterval = 1000;
      this.eventSource.reconnectInterval = this.reconnectInterval;
      if (this.logSseTracing) {
        console.log("reconnectInterval", this.reconnectInterval);
      }
    };
  };

  @action
  parseEvent = (event) => {
    this.lastEventName = event.type;
    const eventData = JSON.parse(event.data);
    this.lastEventData = eventData;
    this.lastEventId = JSON.parse(event.lastEventId);
    if (this.logSseTracing) console.log("lastEventId", this.lastEventId);
    return {
      eventName: event.type,
      eventData: eventData,
    };
  };

  @action
  fetchEventRelatedData = (parsedEvent) => {
    let table = "";
    switch (parsedEvent.eventName) {
      case "TechnicalNotificationEventOnChangedTables":
        const {
          payload: { tablesChanged },
        } = parsedEvent.eventData;
        if (tablesChanged && tablesChanged.length > 0)
          tablesChanged.forEach((table) => {
            this.refreshOpenCMTable(table, parsedEvent.eventData);
            if (parsedEvent.eventData.actionType === "DeleteEntities") {
              this.contentManagementStore.resetSelectedRows(table);
            }
          });
        break;
      case "EntityCreated":
      case "NewEntityVersionCreated":
      case "EntityUpdated":
      case "EntityMarkedForDeletion":
      case "EntityDeleted":
      case "EntityInactivated":
        const { objectType } = parsedEvent.eventData;
        table = objectType;
        this.refreshOpenCMTable(table, parsedEvent.eventData, false);
        break;
      case "MaintenanceAgencyCreated":
        table = "MaintenanceAgency";
        this.refreshOpenCMTable(table, parsedEvent.eventData, true);
        break;
      case "FrameworkCreated":
        table = "Framework";
        this.refreshOpenCMTable(table, parsedEvent.eventData, true);
        this.refreshExportImportStore();
        break;
      case "FrameworkPublished":
        table = "Framework";
        this.refreshOpenCMTable(table, parsedEvent.eventData);
        break;
      case "ProjectCreated":
        this.refreshMAO();
        this.refreshPO();
        this.refreshAllOpenCMTable(parsedEvent.eventData, true);
        break;
      case "ProjectReopened":
      case "ProjectMadeReadyForPromotion":
      case "ProjectPromoted":
        this.refreshProjectData(parsedEvent)
        break;
      case "ProjectClosed":
        this.handleProjectClosedEvent(parsedEvent)
        break;
      case "BranchCreated":
      case "BranchMerged":
        this.refreshPO();
        this.refreshDA();
        break;
      case "ReleaseBuilt":
      case "ReleaseDeleted":
      case "ReleaseUpdated":
        this.refreshMAO();
        this.refreshPO();
        break;
      case "BranchInactivated":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        break;
      case "MaintenanceAgencyOwnershipAssigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        this.refreshSA();
        this.refreshOpenCMTable(
          "MaintenanceAgency",
          parsedEvent.eventData,
          true
        );
        break;
      case "ProjectOwnershipAssigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        this.refreshOpenCMTable("Framework", parsedEvent.eventData, true);
        break;
      case "BranchOwnershipAssigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        break;
      case "DictionaryAdministratorAdded":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        break;
      case "MaintenanceAgencyOwnershipUnassigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        this.refreshSA();
        this.refreshOpenCMTable(
          "MaintenanceAgency",
          parsedEvent.eventData,
          true
        );
        break;
      case "ProjectOwnershipUnassigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        this.refreshOpenCMTable("Framework", parsedEvent.eventData, true);
        break;
      case "BranchUnassigned":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        break;
      case "DictionaryAdministratorRemoved":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        break;
      case "OrchestrationUpdated":
        this.refreshUser();
        this.refreshMAO();
        this.refreshPO();
        this.refreshDA();
        const { type } = parsedEvent.eventData.payload.updatedOrchestration;
        if (type === "MaintenanceAgency" || type === "Framework") {
          this.refreshOpenCMTable(
            parsedEvent.eventData.payload.updatedOrchestration.type,
            parsedEvent.eventData,
            true
          );
        } else if (type === "Actor") {
          this.userStore.fetchAllUsers();
        } else if (type === "Branch") {
          this.refreshAllOpenCMTable(parsedEvent.eventData, true);
        }
        break;
      case "ExcelFileImported":
      case "CsvFileImported":
        this.refreshAllOpenCMTable(parsedEvent.eventData, false, true);
        break;
      case "FrameworkComparisonGenerated":
        if (
          this.loadTimestampInMillis <=
          moment(parsedEvent.eventData.datetime).valueOf()
        ) {
          toastService.notifyAboutFrameworkComparisonChanges(
            parsedEvent.eventData.payload
          );
        }
        break;
      case "HeartBeat":
        if (this.logSseTracing) console.log("HeartBeat, no processing");
        break;
      case "ValidationCompleted":
        this.handleValidationComplete(parsedEvent.eventData.payload)
        break;
      default:
        break;
    }
  };

  refreshUser = () => {
    this.userStore.helper_fetch_UserData();
  };

  refreshOpenCMTable = (table, eventData, isForced) => {
    if (this.contentManagementStore.isTableOpened(table)) {
      // currently refresh only if i am the actor that triggered the action OR refresh is forced since the origin is not CM
      // TODO: consider also if i'm currently editing the table
      const shouldBeTableRefreshed =
        eventData.aid === this.userStore.get_obj_userData.actor.tid;
      if (isForced || shouldBeTableRefreshed) {
        this.contentManagementStore.fetchContentManagementTableData(table);
      }
    }
  };

  refreshAllOpenCMTable = (eventData, isForced, shouldResetSelectedRows) => {
    const openTables = this.contentManagementStore.getOpenedTables;
    const shouldTableBeRefreshed =
      eventData.aid === this.userStore.get_obj_userData.actor.tid;
    if (isForced || shouldTableBeRefreshed) {
      openTables.forEach((table) => {
        this.contentManagementStore.fetchContentManagementTableDataWithCallback(
          table
        );
        if (shouldResetSelectedRows) {
          this.contentManagementStore.resetSelectedRows(table);
        }
      });
    }
  };

  refreshExportImportStore = () => {
    this.exportImportStore.func_fetch_all_frameworks();
  };

  refreshSA = () => {
    this.systemAdministratorStore.func_fetchSystemAdministratorData();
  };

  refreshMAO = () => {
    this.maintenanceAgencyStore.mao_activate_maoStore();
    this.createFrameworkProjectStore.activate_createFramStore();
  };

  refreshPO = () => {
    this.projectOwnerStore.activation_PoStore();
    this.createMappingProjectStore.helper_fetch_po_data();
  };

  refreshDA = () => {
    this.dictionaryAdministratorStore.func_fetchDictionaryAdministratorData();
  };

  handleValidationComplete = ({
    requestId,
    validationIssuer,
    isValidationSuccess,
    message,
    viewLabel
  }) => {
    if (isValidationSuccess) {
      if (!viewLabel || this.validationStore.isValidationsModalVisible(viewLabel)) {
        this.validationStore.fetchValidationResults(requestId, 0, 10)
      }
    } else {
      toastService.func_notify(
        message,
        "error"
      );
    }

    this.validationStore.resetOngoingRequest()
  }

  refreshProjectData = ({ 
    eventData
  }) => {
    this.refreshMAO();
    this.refreshPO();
    this.refreshDA();
    this.refreshAllOpenCMTable(eventData, true);
    this.refreshExportImportStore();
    this.searchStore.fetchFrameworks();
  }

  handleProjectClosedEvent = ({ 
    eventData
  }) => {
    const payload = eventData.payload;
    if (payload.isAutomaticClosure && !payload.closedProject) {
      toastService.func_notify(
        "Couldn't close the project automatically",
        "warn"
      );
    } else {
      this.refreshProjectData({eventData})
    }
  }
}
